import React, { useRef, useEffect } from 'react';
import {
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CBadge, CRow, CCol, CCard, CCardBody
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilMonitor } from '@coreui/icons';
import dayjs from 'dayjs';
import { getAudioSegmentColor } from '../../utils/audioUtils';

// ЗАЩИТНАЯ ФУНКЦИЯ: Превращает объекты в читаемую строку (спасает от ошибки React #31)
const safeString = (val) => {
  if (!val) return null;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return Object.entries(val)
      .map(([k, v]) => `${k}: ${v}`)
      .join('; ');
  }
  return String(val);
};

const formatSec = (sec) => {
  if (!sec && sec !== 0) return '00:00';
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return '';
  return dayjs(isoStr).format('DD.MM.YYYY HH:mm:ss');
};

const AudioDetailModal = ({ visible, onClose, segment, onWatchVideo }) => {
  const audioRef = useRef(null);

  const handleLoadedMetadata = () => {
    if (audioRef.current && segment?.start_sec !== undefined) {
      try {
        audioRef.current.currentTime = segment.start_sec;
        audioRef.current.pause();
      } catch (e) {}
    }
  };

  useEffect(() => {
    if (visible && audioRef.current && segment?.start_sec !== undefined) {
      if (audioRef.current.readyState >= 1) {
        try {
          audioRef.current.currentTime = segment.start_sec;
          audioRef.current.pause();
        } catch (e) {}
      }
    }
  }, [visible, segment]);

  if (!segment) return null;

  const color = getAudioSegmentColor(segment);
  const markers = segment.dialog_markers || {};
  const topics = segment.topics || {};

  const handleWatchClick = () => {
    if (onWatchVideo && segment.start_time) {
      onWatchVideo(segment.start_time);
      onClose();
    }
  };

  return (
    <CModal alignment="center" size="lg" visible={visible} onClose={onClose}>
      <CModalHeader style={{ borderLeft: `8px solid ${color}` }}>
        <CModalTitle>
          🎙️ Фрагмент речи ({formatDateTime(segment.start_time)} — {dayjs(segment.end_time).format('HH:mm:ss')})
        </CModalTitle>
      </CModalHeader>

      <CModalBody>
         {segment.script_compliance && segment.script_compliance.checklist && (
          <CCard className="mb-3 border-top-primary border-top-3 shadow-sm">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold m-0 text-primary">📋 Проверка скрипта продаж</h6>
                <CBadge color={segment.script_compliance.total_score >= 80 ? 'success' : segment.script_compliance.total_score >= 50 ? 'warning' : 'danger'} className="p-2 fs-6">
                  Оценка: {segment.script_compliance.total_score} / 100%
                </CBadge>
              </div>
              <p className="small text-muted mb-3 italic">💡 <strong>Резюме:</strong> {segment.script_compliance.summary_feedback}</p>
              <div className="d-flex flex-column gap-2">
                {segment.script_compliance.checklist.map((item, idx) => (
                  <div key={idx} className={`p-2 rounded border d-flex justify-content-between align-items-center small ${item.passed ? 'border-success' : 'border-danger'}`} style={{ backgroundColor: item.passed ? '#f0fff4' : '#fff5f5' }}>
                    <div>
                      <span className="fw-bold me-2">{item.passed ? '✅' : '❌'} {item.step_name}</span>
                      <span className="text-muted">({item.comment})</span>
                    </div>
                  </div>
                ))}
              </div>
            </CCardBody>
          </CCard>
        )}
        {segment.audio_url && (
          <div className="p-3 bg-light rounded mb-3 border">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="fw-bold m-0">
                Запись звука ({formatDateTime(segment.start_time)}):
              </label>
              <span className="badge bg-primary">Старт: {formatSec(segment.start_sec)}</span>
            </div>
            <audio
              ref={audioRef}
              controls
              src={segment.audio_url}
              className="w-100"
              onLoadedMetadata={handleLoadedMetadata}
            />
            <small className="text-muted d-block mt-1">
              💡 Ползунок установлен на {formatSec(segment.start_sec)}. Нажмите Play для прослушивания.
            </small>
          </div>
        )}

        <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
          <span className="fw-bold">Тональность:</span>
          <CBadge style={{ backgroundColor: color, fontSize: '0.9rem' }}>
            {markers.sentiment || 'Нейтральный'}
          </CBadge>

          {markers.has_profanity_or_aggression && (
            <CBadge color="danger" className="p-2">
              ⚠️ Обнаружена ненормативная лексика / Агрессия
            </CBadge>
          )}

          <CBadge color="secondary" className="p-2">
            Контекст: {segment.context_type}
          </CBadge>
        </div>

        {markers.key_entities && markers.key_entities.length > 0 && (
          <div className="mb-3">
            <strong className="d-block mb-1">Упомянуты в разговоре (имена, должности, объекты):</strong>
            <div className="d-flex gap-1 flex-wrap">
              {markers.key_entities.map((ent, idx) => (
                <CBadge key={idx} color="info" className="text-white">
                  👤 {ent}
                </CBadge>
              ))}
            </div>
          </div>
        )}

        <h6 className="mt-4 mb-2 text-primary border-bottom pb-1">Аналитический разбор темы:</h6>
        <CRow className="g-2 mb-3">
          {safeString(topics.client_interaction) && (
            <CCol md={12}>
              <CCard className="border-start-4 border-start-info">
                <CCardBody className="p-2">
                  <strong>🤝 Общение с клиентом:</strong> {safeString(topics.client_interaction)}
                </CCardBody>
              </CCard>
            </CCol>
          )}

          {safeString(topics.materials_search) && (
            <CCol md={6}>
              <CCard className="border-start-4 border-start-success">
                <CCardBody className="p-2">
                  <strong>🌸 Цветы / Материалы:</strong> {safeString(topics.materials_search)}
                </CCardBody>
              </CCard>
            </CCol>
          )}

          {safeString(topics.order_details) && (
            <CCol md={6}>
              <CCard className="border-start-4 border-start-warning">
                <CCardBody className="p-2">
                  <strong>💐 Детали заказа:</strong> {safeString(topics.order_details)}
                </CCardBody>
              </CCard>
            </CCol>
          )}

          {safeString(topics.management_and_internal) && (
            <CCol md={12}>
              <CCard className="border-start-4 border-start-primary">
                <CCardBody className="p-2">
                  <strong>🏢 Управление и решения:</strong> {safeString(topics.management_and_internal)}
                </CCardBody>
              </CCard>
            </CCol>
          )}

          {safeString(topics.personal_and_atmosphere) && (
            <CCol md={12}>
              <CCard className="border-start-4 border-start-dark bg-light">
                <CCardBody className="p-2">
                  <strong>☕ Личные темы / Атмосфера:</strong> {safeString(topics.personal_and_atmosphere)}
                </CCardBody>
              </CCard>
            </CCol>
          )}

          {safeString(topics.operations) && (
            <CCol md={12}>
              <CCard className="border-start-4 border-start-secondary">
                <CCardBody className="p-2">
                  <strong>🚚 Операции (Касса / Доставка):</strong> {safeString(topics.operations)}
                </CCardBody>
              </CCard>
            </CCol>
          )}
        </CRow>

        <details className="border rounded p-2 bg-light">
          <summary className="fw-bold cursor-pointer text-secondary">
            📜 Оригинальная распознанная речь
          </summary>
          <div className="mt-2 pt-2 border-top font-monospace" style={{ fontSize: '0.85rem' }}>
            {segment.raw_lines && segment.raw_lines.length > 0 ? (
              segment.raw_lines.map((line, i) => (
                <div key={i} className="mb-1">
                  <span className="text-primary me-2">
                    [{dayjs(segment.start_time).add(line.start_sec, 'second').format('HH:mm:ss')}]
                  </span>
                  <span>{line.text}</span>
                </div>
              ))
            ) : (
              <span className="text-muted fst-italic">Оригинальные строки недоступны.</span>
            )}
          </div>
        </details>
      </CModalBody>

      <CModalFooter className="d-flex justify-content-between">
        <CButton color="primary" onClick={handleWatchClick}>
          <CIcon icon={cilMonitor} className="me-1" /> Смотреть видео
        </CButton>
        <CButton color="secondary" onClick={onClose}>Закрыть</CButton>
      </CModalFooter>
    </CModal>
  );
};

export default AudioDetailModal;